/**
 * Score Info Icon Component
 * Displays an info icon that opens a modal with score summary
 */

import { useState } from 'react';
import { Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getSnapshotExplanation, getSwingExplanation, type SnapshotScoringExplanation } from '@/lib/api/scoring';
import { GolfButton } from '@/components/golf/golf-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface ScoreInfoIconProps {
  score: number;
  snapshotId?: string;
  runId?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function ScoreInfoIcon({ score, snapshotId, runId, size = 'md' }: ScoreInfoIconProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState<SnapshotScoringExplanation | null>(null);
  const navigate = useNavigate();

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const handleClick = async () => {
    setOpen(true);
    setLoading(true);
    
    try {
      let data: SnapshotScoringExplanation;
      if (snapshotId) {
        data = await getSnapshotExplanation(snapshotId);
      } else if (runId) {
        data = await getSwingExplanation(runId);
      } else {
        setLoading(false);
        return;
      }
      setExplanation(data);
    } catch (error) {
      console.error('Error loading explanation:', error);
      // Still show modal with basic info
    } finally {
      setLoading(false);
    }
  };

  const handleViewFullBreakdown = () => {
    setOpen(false);
    if (snapshotId) {
      navigate(`/score-explanation/snapshot/${snapshotId}`);
    } else if (runId) {
      navigate(`/score-explanation/swing/${runId}`);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors [&_svg]:stroke-current"
        aria-label="View score explanation"
      >
        <Info className={sizeClasses[size]} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Score Breakdown</DialogTitle>
            <DialogDescription>
              Your swing quality score: {score.toFixed(1)}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : explanation ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Peer Comparison</p>
                <p className="text-sm text-muted-foreground">
                  {explanation.peer_label.replace(/_/g, ' ')} for {explanation.skill_level} players
                </p>
              </div>

              {explanation.top_3_faults.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Top Contributing Faults</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {explanation.top_3_faults.slice(0, 3).map((fault, idx) => (
                      <li key={idx}>
                        {idx + 1}. {fault.taxonomy_key.replace(/_/g, ' ')} 
                        {' '}(severity {fault.median_severity}, {fault.consistency}/5 swings)
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-2">
                  Only the top 3 most consistent faults affect your score. Other faults are shown for curriculum mapping but don't affect the score.
                </p>
                <GolfButton
                  onClick={handleViewFullBreakdown}
                  className="w-full"
                  size="sm"
                >
                  View Full Breakdown
                </GolfButton>
              </div>
            </div>
          ) : (
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Unable to load detailed explanation. Your score is {score.toFixed(1)}.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

